import { useMarket } from "../lib/store";
import { usePortfolio } from "../lib/usePortfolio";
import { useSubscribe } from "../lib/socket";
import { fmt, inr, signed, tone, pct } from "../lib/format";
import { Panel, PageHeader, Metric, th, td, btnGhost, Empty } from "../components/Panel";
import { Price, StaleTag } from "../components/Price";

export default function Holdings() {
  const rows = usePortfolio("/holdings") || [];
  const ticks = useMarket((s) => s.ticks);
  const openOrder = useMarket((s) => s.openOrder);
  useSubscribe(rows.map((h) => h.instrument_key));
  const calc = rows.map((h) => {
    const t = ticks[h.instrument_key];
    const ltp = t?.ltp ?? h.avg_price;
    return { h, t, ltp, inv: h.qty * h.avg_price, cur: h.qty * ltp, day: t?.cp ? (ltp - t.cp) * h.qty : 0 };
  });
  const inv = calc.reduce((a, r) => a + r.inv, 0);
  const cur = calc.reduce((a, r) => a + r.cur, 0);
  const day = calc.reduce((a, r) => a + r.day, 0);
  return (
    <div className="p-4 lg:p-6" data-testid="holdings-page">
      <PageHeader title={`Holdings (${rows.length})`} />
      <Panel className="mb-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-border">
          <Metric label="Total investment" value={inr(inv)} testid="holdings-investment" />
          <Metric label="Current value" value={inr(cur)} testid="holdings-current" />
          <Metric label="Day's P&L" value={<span className={tone(day)}>{signed(day)}</span>} testid="holdings-day-pnl" />
          <Metric label="Total P&L" value={<span className={tone(cur - inv)}>{signed(cur - inv)}</span>} sub={inv ? pct(((cur - inv) / inv) * 100) : ""} testid="holdings-total-pnl" />
        </div>
      </Panel>
      <Panel>
        {!rows.length ? <Empty testid="holdings-empty">No holdings. Buy equity with CNC (delivery) to build your portfolio.</Empty> : (
          <div className="overflow-x-auto thin-scroll">
            <table className="w-full" data-testid="holdings-table">
              <thead className="border-b border-border"><tr>
                {["Instrument", "Qty", "Avg cost", "LTP", "Cur. value", "P&L", "Net chg", "Day chg", ""].map((h, i) => <th key={i} className={`${th} ${i > 0 && i < 8 ? "text-right" : ""}`}>{h}</th>)}
              </tr></thead>
              <tbody>
                {calc.map(({ h, t, cur: c, inv: iv }) => (
                  <tr key={h.id} className="border-b border-border/60 hover:bg-accent/40" data-testid={`holding-row-${h.symbol}`}>
                    <td className={`${td} font-medium`}>{h.symbol} {h.t1_qty > 0 && <span className="ml-1 text-[9px] font-semibold text-amber-500 border border-amber-500/40 rounded px-1">T1: {h.t1_qty}</span>}</td>
                    <td className={`${td} num text-right`}>{h.qty}</td>
                    <td className={`${td} num text-right`}>{fmt(h.avg_price)}</td>
                    <td className={`${td} text-right`}><Price value={t?.ltp} prev={t?.prevLtp} /><StaleTag /></td>
                    <td className={`${td} num text-right`}>{fmt(c)}</td>
                    <td className={`${td} num text-right font-semibold ${tone(c - iv)}`}>{signed(c - iv)}</td>
                    <td className={`${td} num text-right ${tone(c - iv)}`}>{iv ? pct(((c - iv) / iv) * 100) : "—"}</td>
                    <td className={`${td} num text-right ${tone(t?.chp)}`}>{pct(t?.chp)}</td>
                    <td className={`${td} text-right`}><span className="flex justify-end gap-1">
                      <button className={btnGhost} onClick={() => openOrder({ key: h.instrument_key, side: "BUY", product: "CNC" })} data-testid={`holding-buy-${h.symbol}`}>Buy</button>
                      <button className={`${btnGhost} text-down`} onClick={() => openOrder({ key: h.instrument_key, side: "SELL", product: "CNC", qty: h.qty })} data-testid={`holding-sell-${h.symbol}`}>Sell</button>
                    </span></td>
                  </tr>))}
              </tbody>
            </table>
          </div>)}
      </Panel>
    </div>
  );
}
